/**
 * `employee.privilege` -- the one role fact the Backend actually stores.
 *
 * The `privilege` table holds exactly two rows: 1 = Admin, 2 = Sale. The
 * employee form's three role tiles (`manager`, `cashier`, `custom`) and its
 * permission badges are NOT stored anywhere -- the `employee` table has no
 * role or permission column -- so `privilege` is the only thing that
 * survives a save, and the only thing the Backend decides with (Device
 * session revoke requires `privilege === 1`).
 *
 * WHY THIS FILE EXISTS
 * ---------------------------------------------------------------
 * The form used to send `role` and never `privilege`, and `employee.tsx`
 * defaulted a missing value to 2. So choosing "ผู้จัดการร้านค้า" and saving
 * changed nothing, and there was no normal way to make anybody Admin.
 * Fixing that means deriving privilege from role in one direction and role
 * from privilege in the other, and doing it with the SAME strict parsing
 * the Device permission check already uses -- so this is the single place
 * both of those, and the Device helper, read from.
 *
 * WHAT MAY CHANGE PRIVILEGE, AND WHAT MUST NOT
 * ---------------------------------------------------------------
 * Only an explicit choice of the manager or cashier tile. `custom` means
 * "a hand-picked set of permission badges", and those badges are display
 * only, so it keeps whatever privilege the employee already has. Without
 * that rule, toggling one cosmetic badge on an Admin's form and pressing
 * save would silently demote them, and the "เลือกทั้งหมด" checkbox (which
 * used to flip the role to `manager`) would silently promote anyone.
 */

/** `privilege`.`id` of the Admin row. Mirrors `PRIVILEGE_ADMIN` in `common/permission.py`. */
export const PRIVILEGE_ADMIN = 1;

/** `privilege`.`id` of the Sale row -- the ordinary employee. */
export const PRIVILEGE_SALE = 2;

export type EmployeeRole = 'manager' | 'cashier' | 'custom';

/**
 * ASCII digits only, matching `_PRIVILEGE_ID_PATTERN` in the Backend.
 * `Number()` alone is too generous: `Number(true)` is 1, `Number([])` is 0
 * and `Number('1.0')` is 1 -- each of which the Backend refuses.
 */
const PRIVILEGE_ID = /^-?[0-9]{1,9}$/;

/**
 * A privilege id as an integer, or null for anything that is not one.
 *
 * Only an integer, or a string of ASCII digits (JSON round-trips and drivers
 * both produce those), is considered at all. Booleans, floats, `'1.0'`,
 * non-ASCII numerals and empty values are null -- never coerced.
 */
export const parsePrivilege = (value: unknown): number | null => {
    if (typeof value === 'number') {
        return Number.isInteger(value) ? value : null;
    }
    if (typeof value === 'string' && PRIVILEGE_ID.test(value.trim())) {
        return Number(value.trim());
    }
    return null;
};

/** Whether [value] is, strictly, the Admin privilege id. */
export const isAdminPrivilege = (value: unknown): boolean =>
    parsePrivilege(value) === PRIVILEGE_ADMIN;

/**
 * The role tile to show for a stored privilege.
 *
 * Anything that is not strictly Admin -- including a missing, malformed or
 * unknown value from an old record -- shows as `cashier`, so opening a form
 * can never make somebody look like, or become, an Admin by accident.
 */
export const roleForPrivilege = (value: unknown): 'manager' | 'cashier' =>
    isAdminPrivilege(value) ? 'manager' : 'cashier';

/**
 * The privilege to persist for a role choice.
 *
 * `manager` -> Admin, `cashier` -> Sale. `custom` (and any unrecognised role)
 * keeps [current] -- normalised, so a malformed current value becomes Sale
 * rather than surviving to the Backend or turning into Admin.
 */
export const privilegeForRole = (role: unknown, current: unknown): number => {
    if (role === 'manager') return PRIVILEGE_ADMIN;
    if (role === 'cashier') return PRIVILEGE_SALE;
    return persistablePrivilege(current);
};

/**
 * The last word before a save: Admin only if the value is strictly Admin,
 * otherwise Sale.
 *
 * `employee.tsx` used `isEmpty(privilege) ? 2 : privilege`, and `isEmpty`
 * only recognises undefined/null/'' -- so `true`, `1.9` or `'x'` went to the
 * Backend as-is, and the Backend binds whatever it is given. Only two
 * privilege rows exist; if a third is ever added this function is the one
 * place that has to learn it.
 */
export const persistablePrivilege = (value: unknown): number =>
    isAdminPrivilege(value) ? PRIVILEGE_ADMIN : PRIVILEGE_SALE;
